import React, { useEffect } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { DateTime, Duration } from 'luxon';
import { RichText } from 'prismic-reactjs';
import { Row, Col, Badge, Card } from 'react-bootstrap';
import camelcaseKeys from 'camelcase-keys';
// import Prismic from 'prismic-javascript';
import Layout from '../../../components/Layout';
import Head from '../../../components/head';
import OpenGraphTitle from '../../../components/OpenGraphTitle';
import OpenGraphDescription from '../../../components/OpenGraphDescription';
import OpenGraphKeywords from '../../../components/OpenGraphKeywords';
import OpenGraphImage from '../../../components/OpenGraphImage';
import Section from '../../../components/Section';
import Gataware from '../../../SDKs/Gataware';
import { FranchiseeProvider } from '../../../context/Franchisee';
import { FranchiseeState } from '../../../context/Franchisee/franchisee.types';
import { PrismicClient } from '../../../SDKs/Prismic/prismic.config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faTwitter, faPinterest } from '@fortawesome/free-brands-svg-icons';
import PageTitle from '../../../components/PageTitle';

const anonAvatar = '/images/anonymous-avatar.svg';

export const getStaticPaths: GetStaticPaths = async () => {
  // const queryResponse = await PrismicClient()
  //   .query(Prismic.Predicates.at('document.type', 'blog-post'));

  // const paths = queryResponse.results
  //   .filter((blogPost) => blogPost.uid !== null)
  //   .map(({ uid }) => ({ params: { uid } }));

  const paths = [
    { params: { uid: 'clean-savings', franchiseeSlug: 'birmingham' } },
  ];

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const uniqueId: string = context.params.uid.toLocaleString();

  const { franchiseeSlug } = context.params;

  const response = await Gataware.getLocations({
    slugs: [franchiseeSlug.toLocaleString()],
  }).then(({ data }) => JSON.parse(data));

  const franchisees = await response.data
    .filter(({ slug }) => franchiseeSlug === slug)
    .map((franchisee) => camelcaseKeys(franchisee));

  if (!franchisees) {
    return {
      notFound: true,
      revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
    };
  }

  const [franchisee] = franchisees;

  const blogPost = await PrismicClient().getByUID('blog-post', uniqueId, {});

  if (!blogPost) {
    return {
      notFound: true,
      // revalidate: Duration.fromObject({ minutes: 15 }).as('seconds'),
      revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
    };
  }

  return {
    props: {
      blogPost,
      franchisee,
    },
    // revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
    revalidate: Duration.fromObject({ hours: 8 }).as('seconds'),
  };
};

interface BlogPageProps {
  /* add blog page props here */
  blogPost: any;
  franchisee: FranchiseeState;
}

const Blog: React.FC<BlogPageProps> = ({
  blogPost,
  franchisee,
}: BlogPageProps) => {
  const path = useRouter().asPath;

  const publishedDate = DateTime.fromISO(blogPost.data.dateposted);
  const fallbackDate = DateTime.fromISO(blogPost.first_publication_date);
  // const editedDate = DateTime.fromISO(blogPost.last_publication_date);

  const { banner, blogTitle, content } = blogPost.data;

  const blogPostData = blogPost.data;

  const authorName = blogPost.data['author-name'];
  const authorPhoto = blogPost.data['author-photo'];

  const title = blogTitle[0]?.text;

  return (
    <FranchiseeProvider franchiseeValue={franchisee}>
      <Layout alternateFooter>
        <Head
          slug={`${title}`}
          title={`${title} | Two Maids ${franchisee.name}`}
          description={`Two Maids ${franchisee.name}'s Blog.`}
        />
        <OpenGraphTitle title={`${title} | Two Maids`} />

        <OpenGraphDescription>
          {/* Add Blog description here */}
          {`Two Maids ${franchisee.name}'s Blog.`}
        </OpenGraphDescription>
        {blogPost.tags.length > 0 && (
          <OpenGraphKeywords keywords={blogPost.tags} />
        )}
        {banner?.url && <OpenGraphImage source={banner.url} alt={title} />}        
          <Section padding='none' className='pt-5 single-blog-page'>
              <Col
                xs={12}
                md={12}
                className='d-flex flex-column justify-content-center mb-3 blog-heading-section'
              >
                <h1
                  className='blog-title'
                >
                  {title}
                </h1>

                <span style={{ fontSize: 14, color: 'var(--dark-pink)' }}>
                  {blogPost.data.dateposted === null
                    ? fallbackDate.toFormat('MM/d/yyyy')
                    : publishedDate.toFormat('MM/d/yyyy')}{' '}
                </span>
                <div className='d-flex mt-3'>
                  <div className='d-flex flex-wrap'>
                    {blogPost.tags.length > 0 &&
                      blogPost.tags.map((tag) => (
                        <Badge
                          key={tag}
                          style={{ fontSize: '14px',
                          color: 'var(--dark-pink)',
                          fontFamily: 'Gotham Medium',
                          textTransform: 'uppercase' }}
                          className='mb-2 pb-1'
                        >
                          {tag}
                        </Badge>
                      ))}
                  </div>
                </div>
              </Col>
              {banner?.url && (
                <img
                  src={banner.url}
                  alt={banner.alt}
                  className='blog-banner-img'
                />
              )}
            
            <div className='d-flex flex-row justify-content-start align-items-center'>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=https://twomaidscleaning.com${path}`} target="_blank" rel='noreferrer'>
                <FontAwesomeIcon
                  icon={faFacebook}
                  className='mr-2 my-2 blog-social-link'
                  />
              </a>


              <a href={`https://twitter.com/intent/tweet?url=https://twomaidscleaning.com${path}`} target="_blank" rel='noreferrer'>
                <FontAwesomeIcon
                  icon={faTwitter}
                  className='mr-2 my-2 blog-social-link'
                  />
              </a>

              <a href={`http://pinterest.com/pin/create/button/?url=https://twomaidscleaning.com${path}`} target="_blank" rel='noreferrer'>
                <FontAwesomeIcon
                  icon={faPinterest}
                  className='mr-2 my-2 blog-social-link'
                  />
              </a>
            </div>
          </Section>

        <Section className='single-blog-content'>
          <Row className='justify-content-center'>
            <Col xs={12} md={12}>
            {blogPostData.body.map((slice, sliceIndex) => {
                if (slice.items.length === 0) {
                  return null;
                }

                return slice.items.map((sliceItem, itemIndex) => {
                  if (sliceItem === undefined) {
                    return null;
                  }

                  if (slice.slice_type === 'image' && (sliceItem.optional_heading.length >= 1 || sliceItem.optional_text.length >= 1)) {
                    return (
                      <section
                        key={`${sliceIndex}-${itemIndex}`}
                        className='d-flex justify-content-between align-items-center flex-wrap flex-lg-nowrap mb-4'
                      >
                        <img
                          className='w-full w-50 h-10 mx-auto pr-4 mb-4 dynamic-page-image-with-text'
                          src={sliceItem.image.url}
                        />
                        <div>
                          {RichText.render(sliceItem.optional_heading)}
                          {RichText.render(sliceItem.optional_text)}
                        </div>
                      </section>
                    );
                  } else if (slice.slice_type === 'image') {
                    return (
                      <img
                        key={`${sliceIndex}-${itemIndex}`}
                        className='mb-4 dynamic-page-image'
                        src={sliceItem.image.url}
                      />
                    );
                  } else if (slice.slice_type === 'image_flip' && (sliceItem.optional_heading.length >= 1 || sliceItem.optional_text.length >= 1)) {
                    return (
                      <section
                        key={`${sliceIndex}-${itemIndex}`}
                        className='d-flex flex-row-reverse justify-content-between align-items-center flex-wrap flex-lg-nowrap mb-4'
                      >
                        <img
                          className='w-full w-50 h-10 mx-auto pr-4 mb-4 dynamic-page-image-with-text'
                          src={sliceItem.image.url}
                        />
                        <div>
                          {RichText.render(sliceItem.optional_heading)}
                          {RichText.render(sliceItem.optional_text)}
                        </div>
                      </section>
                    );
                  } else if (slice.slice_type === 'image_flip') {
                    return (
                      <img
                        key={`${sliceIndex}-${itemIndex}`}
                        className='mb-4 dynamic-page-image'
                        src={sliceItem.image.url}
                      />
                    );
                  } else if (slice.slice_type === 'text') {
                    return (
                      <div key={`${sliceIndex}-${itemIndex}`} className='mb-4 blog-content-container'>
                        {RichText.render(sliceItem.heading)}
                        <p className={`${itemIndex === 0 ? 'blog-text-content': ''}`}>{RichText.render(sliceItem.text)}</p>
                      </div>
                    );
                  } else if (slice.slice_type === 'embed') {
                    return (
                      <div
                        key={`${sliceIndex}-${itemIndex}`}
                        className='dynamic-page-embed mb-4'
                        dangerouslySetInnerHTML={{ __html: sliceItem.embed.html }}
                      />
                    );
                  }

                  return null;
                });
              })}
            </Col>
          </Row>
        </Section>
      </Layout>
    </FranchiseeProvider>
  );
};

export default Blog;
